import type { Theme, SxProps, SvgIconProps } from '@mui/material';
import type { CountryOption } from 'src/components/country-select';

import { CSS } from '@dnd-kit/utilities';
import { useRef , useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { varAlpha } from 'minimal-shared/utils';

import { Box, Chip, Tooltip, Checkbox, Typography } from '@mui/material';

import { countries } from 'src/assets/data/countries';

import { SvgColor } from 'src/components/svg-color';

import type { Skill, JobDetails, RoleDetails, EditableField } from './types';

export const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} />
);

// Validation functions
export const validateExperience = (value: string): boolean => {
  // Regex1: Match numbers separated by -, |, 'to', or comma
  const regex1 = /^(\d+)\s*[-|,]\s*(\d+)$|^(\d+)\s+to\s+(\d+)$/i;

  // Regex2: Match plus format (e.g., "+ 5", "5 +", "plus 5", "5 plus", "5+", "+5")
  const regex2 = /^(\+|\d+)\s*(\d+)?\s*(\+)?$|^(\d+)\s*(plus|\+)\s*(\d+)?$|^(plus|\+)\s*(\d+)$/i;

  return regex1.test(value) || regex2.test(value);
};

export const validateSalary = (value: string): boolean => {
  // Match salary formats: "120k", "120k-150k", "120000", "120000-150000"
  const regex = /^(\d+k?)\s*[-|,]\s*(\d+k?)$|^(\d+k?)$/i;
  return regex.test(value);
};

// Processing functions
export const processExperience = (jobDetails: EditableField): string => {
  const value = jobDetails.tempValue;
  if (validateExperience(value)) {
    // Process range format
    if (value.match(/^(\d+)\s*[-|,]\s*(\d+)$|^(\d+)\s+to\s+(\d+)$/i)) {
      let firstNum: string, secondNum: string;

      if (value.toLowerCase().includes('to')) {
        [firstNum, secondNum] = value.toLowerCase().split(/\s+to\s+/);
      } else {
        [firstNum, secondNum] = value.split(/[-|,]/);
      }

      return `${firstNum.trim()} - ${secondNum.trim()}`;
    }

    // Process plus format
    const matches = value.match(/\d+/);
    if (matches) {
      return `${matches[0].trim()} +`;
    }
  }

  return jobDetails.value;
};

export const processSalary = (jobDetails: EditableField): string => {
  const value = jobDetails.tempValue;
  if (validateSalary(value)) {
    // Convert numbers to k format if they're in thousands
    const convertToK = (num: string): string => {
      const numValue = parseInt(num.replace('k', ''), 10);
      return numValue >= 1000 ? `${Math.floor(numValue / 1000)}k` : numValue.toString();
    };

    // Handle range format
    if (value.includes('-') || value.includes('|') || value.includes(',')) {
      const [first, second] = value.split(/[-|,]/);
      return `${convertToK(first.trim())}-${convertToK(second.trim())}`;
    }

    // Handle single value
    return convertToK(value.trim());
  }
  return jobDetails.value;
};

export const JOB_DETAILS: JobDetails = {
  Experience: {
    value: '',
    editable: false,
    tempValue: '',
  },
  Salary: {
    value: '',
    editable: false,
    tempValue: '',
  },
  Department: {
    value: '',
    editable: false,
    tempValue: '',
  },
};

export const ROLE_DETAILS: RoleDetails = {
  Title: {
    value: '',
    editable: false,
    tempValue: '',
  },
  LocationCountry: {
    value: '',
    editable: false,
    tempValue: '',
  },
  LocationCity: {
    value: '',
    editable: false,
    tempValue: '',
  },
  JobMode: {
    value: '',
    editable: false,
    tempValue: '',
  },
  JobType: {
    value: '',
    editable: false,
    tempValue: '',
  },
};

export const SortableChip = ({ id, name, onDelete }: any) => {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id,
  });
  return (
    <Chip
      label={name}
      key={id}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      variant="soft"
      color="default"
      size="small"
      sx={{ cursor: 'move', mr: 1.5, mb: 1.5 }}
      onDelete={() => onDelete && onDelete(id)}
    />
  );
};

export const PerksCard = ({
  perkKey,
  value,
  description,
  IconComponent,
  perksList,
  setPerksList,
}: {
  perkKey: string;
  value: string;
  description: string;
  IconComponent: React.FC<SvgIconProps>;
  perksList: any[];
  setPerksList: any;
}) => {
  const handleCardClick = () => {
    const isSelected = perksList?.includes(perkKey);
    setPerksList((prevState: any) =>
      isSelected
        ? { ...prevState, newPerks: prevState.newPerks.filter((key: any) => key !== perkKey) }
        : { ...prevState, newPerks: [...prevState.newPerks, perkKey] }
    );
  };

  const stylePerkOuterContainer = {
    display: 'flex',
    height: 150,
    flexDirection: 'column',
    backgroundColor: (theme: Theme) =>
      perksList?.includes(perkKey)
        ? varAlpha(theme.vars.palette.primary['mainChannel'], 0.08)
        : varAlpha(theme.vars.palette.grey['500Channel'], 0.1),
    borderRadius: 2,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: {
      xs: '1 1 100%', // Full width on extra small screens
      sm: '1 1 calc(50% - 16px)', // Half width on small screens
      md: '1 1 calc(33.33% - 16px)', // One-third width on medium screens
      lg: '1 1 calc(25% - 16px)', // One- forth width on large screens
      xl: '1 1 calc(20% - 16px)', // One- fifth width on extra large screens
    },
    maxWidth: {
      xs: '100%',
      sm: 'calc(50% - 16px)',
      md: 'calc(33.33% - 16px)',
      lg: 'calc(25% - 16px)',
      xl: 'calc(20% - 16px)',
    },
    p: 2,
    border: (theme: Theme) =>
      perksList?.includes(perkKey)
        ? `1px solid ${varAlpha(theme.vars.palette.primary['mainChannel'], 0.2)}`
        : 'none',
    cursor: 'pointer',
    '&:hover': {
      '& .perk-description': {
        display: 'block',
      },
      backgroundColor: (theme: Theme) => varAlpha(theme.vars.palette.primary['mainChannel'], 0.08),
    },
  };
  const stylePerkInnerContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  };
  const styleTypography = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: 2,
  };
  return (
    <Box key={perkKey} sx={stylePerkOuterContainer} onClick={handleCardClick}>
      <Box sx={stylePerkInnerContainer}>
        <IconComponent />
        <Checkbox
          checked={perksList?.includes(perkKey) ?? false}
          // onClick={(e) => e.stopPropagation()}
          readOnly
        />
      </Box>
        <Typography variant="body1">
          {value}
        </Typography>
      <Tooltip title={description}>
      <Typography variant="body2" sx={styleTypography} color="text.secondary">
          {description}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export const OverLayStyleBottom: React.FC = () => (
  <Box
    sx={{
      pointerEvents: 'none',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 60,
      zIndex: 2,
      background: `linear-gradient(
    to bottom,
    rgba(246, 247, 248, 0) 0%,
    rgba(246, 247, 248, 0.25) 25%,
    rgba(246, 247, 248, 0.92) 92%
  )`,
    }}
  />
);

export const styleSection = {
  gap: 3,
  p: 3,
  borderRadius: 2,
  backgroundColor: (theme: Theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
};

// TODO: To be used after discussion with backend team
export const preProcessSkills = (skills: string[]) => skills?.map((skill, index) => ({
  id: index,
  name: skill,
}));

// TODO: To be used after discussion with backend team
export const postProcessSkills = (skills: Skill[]) => skills.map((skill) => skill.name);

export const generateSalaryString = (salaryData: {
  salaryMin?: number | string;
  salaryMax?: number | string;
  salaryType?: string;
  salaryCurrencyCode?: string;
}): string => {
  const { salaryMin, salaryMax, salaryType, salaryCurrencyCode } = salaryData;

  if (!salaryMin && !salaryMax) return '-';

  const currency = salaryCurrencyCode || '';

  if (salaryMin && salaryMax) {
    return `${currency}${salaryMin} - ${currency}${salaryMax}/${salaryType || ''}`;
  }

  if (salaryMin) {
    return `${currency}${salaryMin}/${salaryType || ''}`;
  }

  if (salaryMax) {
    return `${currency}${salaryMax}/${salaryType || ''}`;
  }

  return '-';
};

export const getCountry = (inputValue: string) => {
  const country = countries.find((op) => op.label === inputValue || op.code === inputValue);
  return {
    code: country?.code || '',
    label: country?.label || '',
  };
};

export const getCountryFromString = (countryString: string): CountryOption | null => {
  if (!countryString) return null;
  const country = countries.find(
    (c) => c.label === countryString || c.code === countryString
  );
  return country ? { code: country.code, label: country.label, phone: country.phone } : null;
};

export const EditJobButton = ({ handleOnClick, Icon, sx, color }: { handleOnClick: () => void, Icon: React.JSX.Element, sx?: SxProps, color?: 'default' | 'error' | 'success' }) => (
  <Chip
    size="small"
    variant="soft"
    color={color || 'default'}
    sx={{
      padding: 0,
      width: 28,
      height: 28,
      borderRadius: '50%',
      '& .MuiChip-label': {
        display: 'none'
      },
      '& .MuiChip-icon': {
        margin: 0,
        width: 16,
        height: 16
      },
      ...sx
    }}
    icon={Icon}
    label=""
    onClick={handleOnClick}
  />
)

// snapTo: number of pixels to snap to when scrolling up or down
export function useSnapScroll(snapTo: number) {
  const lastScrollY = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSnapping = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isSnapping.current) return;

      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      // Trigger only if in first snapTo PX
      if (currentY <= snapTo) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (delta > 0) {
            // Scroll down → snap to given PX
            isSnapping.current = true;
            window.scrollTo({ top: snapTo, behavior: 'smooth' });
          } else if (delta < 0) {
            // Scroll up → snap to 0px
            isSnapping.current = true;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }

          // Allow snap again after smooth scroll completes
          setTimeout(() => {
            isSnapping.current = false;
          }, 300); // match your scroll duration
        }, 50); // debounce short enough to allow slight finger flicks
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
}
